import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft, faChevronRight, faCircle } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { Button } from '@/modules/Button';
import { Icon } from '@/modules/Icon';
import { LegacyIcon } from '@/modules/LegacyIcon';
import { ListItemDefault } from '@/modules/Lists';
import { BUTTON_SLOT_SPREAD_CENTER } from '@/shared/types/button';
import type { LegacyIconName } from '@/shared/types/legacyIcon';
import { cn } from '@/shared/utils/cn';
import type { SidebarDrilldownPanelProps, SidebarDrilldownSubmenuItem } from '../../types';
import { SidebarDivider } from '../atoms';

function isFontAwesomeIconDefinition(icon: unknown): icon is IconDefinition {
  if (typeof icon !== 'object' || icon === null) return false;
  return Array.isArray((icon as IconDefinition).icon);
}

/**
 * Second-level sidebar region: back control, optional breadcrumb, and submenu rows.
 * Presentation-only: routing and `href` handling belong to the host via `onActivateSubmenu`.
 *
 * @example
 * ```tsx
 * <SidebarDrilldownPanel
 *   expanded
 *   breadcrumbRootLabel="Campaigns"
 *   parentItem={{ title: "Tools", submenus: [{ title: "Map", href: "#/map" }] }}
 *   onBack={() => {}}
 *   isSubmenuActive={(s) => s.href === "#/map"}
 *   onActivateSubmenu={(s) => navigate(s.href)}
 * />
 * ```
 */
export const SidebarDrilldownPanel: React.FC<SidebarDrilldownPanelProps> = ({
  expanded,
  parentItem,
  breadcrumbRootLabel,
  isSubmenuActive = () => false,
  showBreadcrumbRoot = true,
  onActivateSubmenu,
  onBack,
  backButtonTooltip = 'Back',
  className,
}) => {
  const submenus = parentItem.submenus ?? [];

  const handleActivate = (item: SidebarDrilldownSubmenuItem) => {
    item.onClick?.();
    onActivateSubmenu?.(item);
  };

  const resolveCollapsedButtonIcon = (
    icon: unknown,
    active: boolean
  ): IconDefinition | React.ReactElement => {
    if (isFontAwesomeIconDefinition(icon)) {
      return icon;
    }
    if (typeof icon === 'string' && icon.trim() !== '') {
      return (
        <LegacyIcon
          name={icon as LegacyIconName}
          size="sm"
          color="current"
          className={active ? 'ui-text-on-color' : 'ui-text-icon-default'}
        />
      );
    }
    return faCircle;
  };

  return (
    <div
      className={cn(
        expanded
          ? 'ui-flex ui-w-full ui-min-w-0 ui-flex-col ui-gap-8'
          : 'ui-flex ui-w-full ui-flex-col ui-items-center ui-gap-4',
        className
      )}
    >
      {expanded ? (
        <>
          <button
            type="button"
            onClick={onBack}
            className={cn(
              'ui-flex ui-h-32 ui-w-full ui-min-w-0 ui-cursor-pointer ui-items-center ui-gap-2.5',
              'ui-rounded-control-sm ui-border-0 ui-bg-transparent ui-p-0 ui-text-left',
              'ui-outline-none ui-transition-colors hover:ui-bg-fill-hover',
              'focus-visible:ui-ring-brand focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-offset-2'
            )}
          >
            <span
              className={cn(
                'ui-inline-flex ui-h-32 ui-w-32 ui-shrink-0 ui-items-center ui-justify-center',
                'ui-rounded-control-sm ui-text-icon-default'
              )}
            >
              <Icon name={faArrowLeft} size="sm" color="current" />
            </span>
            <span className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-gap-4">
              {showBreadcrumbRoot ? (
                <span className="ui-inline-flex ui-shrink-0 ui-items-center ui-gap-4">
                  <span
                    className={cn(
                      'ui-whitespace-nowrap ui-text-body-xs-bold ui-font-body-xs-bold',
                      'ui-leading-body-xs-bold ui-tracking-body-xs ui-text-tertiary'
                    )}
                  >
                    {breadcrumbRootLabel}
                  </span>
                  <Icon name={faChevronRight} size="sm" color="tertiary" />
                </span>
              ) : null}
              <span
                className={cn(
                  'ui-min-w-0 ui-flex-1 ui-truncate ui-text-left',
                  'ui-text-body-xs-bold ui-font-body-xs-bold ui-leading-body-xs-bold ui-tracking-body-xs ui-text-brand'
                )}
              >
                {parentItem.title}
              </span>
            </span>
          </button>
          <SidebarDivider className="ui-py-0" />
        </>
      ) : (
        <div className="ui-flex ui-w-full ui-justify-center ui-py-2">
          <Button
            theme="solid"
            color="tertiary"
            size="xs"
            square
            iconProps={{ icon: faArrowLeft, show: true }}
            tooltipProps={{ label: backButtonTooltip, position: 'right', show: true }}
            onClick={onBack}
          />
        </div>
      )}

      <ul
        className={cn(
          'ui-m-0 ui-list-none ui-p-0',
          expanded
            ? 'ui-flex ui-w-full ui-min-w-0 ui-flex-col ui-gap-4'
            : 'ui-flex ui-w-full ui-flex-col ui-items-center ui-gap-4'
        )}
      >
        {submenus.map((item, index) => {
          const subActive = isSubmenuActive(item);
          const key = item.id ?? `drilldown-sub-${parentItem.title}-${index}-${item.title}`;

          const handleRowClick = () => {
            handleActivate(item);
          };

          if (!expanded) {
            return (
              <li key={key} className="ui-flex ui-w-full ui-justify-center">
                <Button
                  slotLayout={BUTTON_SLOT_SPREAD_CENTER}
                  theme="solid"
                  color={subActive ? 'primary' : 'tertiary'}
                  size="xs"
                  square
                  tooltipProps={{ label: item.title, position: 'right', show: true }}
                  icon={resolveCollapsedButtonIcon(item.icon, subActive)}
                  onClick={handleRowClick}
                />
              </li>
            );
          }

          if (isFontAwesomeIconDefinition(item.icon)) {
            return (
              <ListItemDefault
                key={key}
                icon={item.icon}
                current={subActive}
                onClick={handleRowClick}
                className="ui-h-32"
              >
                <span className="ui-flex-1 ui-truncate ui-font-body-sm-medium">{item.title}</span>
              </ListItemDefault>
            );
          }

          if (typeof item.icon === 'string' && item.icon.trim() !== '') {
            return (
              <ListItemDefault
                key={key}
                current={subActive}
                onClick={handleRowClick}
                className="ui-h-32"
              >
                <span className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-x-8">
                  <span
                    className={cn(
                      'ui-flex ui-size-16 ui-shrink-0 ui-items-center ui-justify-center ui-transition-colors ui-duration-300',
                      subActive ? 'ui-text-on-color' : 'ui-text-secondary'
                    )}
                  >
                    <LegacyIcon
                      name={item.icon as LegacyIconName}
                      size="sm"
                      color="current"
                      className="ui-block ui-max-h-full ui-max-w-full"
                    />
                  </span>
                  <span className="ui-flex-1 ui-truncate ui-font-body-sm-medium">{item.title}</span>
                </span>
              </ListItemDefault>
            );
          }

          return (
            <ListItemDefault
              key={key}
              icon={faCircle}
              current={subActive}
              onClick={handleRowClick}
              className="ui-h-32"
            >
              <span className="ui-flex-1 ui-truncate ui-font-body-sm-medium">{item.title}</span>
            </ListItemDefault>
          );
        })}
      </ul>
    </div>
  );
};

SidebarDrilldownPanel.displayName = 'SidebarDrilldownPanel';
